import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Entity } from '../../../../classes/entity.class';
import { CodaltComponent } from '../../../../codalt.component';
import { EntitiesService } from '../../../../services/entities/entities.service';
import { EntityField, EntityType } from '../../../../services/entities/entity-type.class';
import { EntityTypeService, PheFormData, PlanningFormData } from '../../../../services/entity-type.service';

@Component({
    selector: 'app-trackline-planning-has',
    standalone: false,
    templateUrl: './trackline-planning-has.component.html',
    styleUrl: './trackline-planning-has.component.scss',
})
export class TracklinePlanningHasComponent extends CodaltComponent implements OnInit {
    @Input() fieldFormData: PlanningFormData;
    @Input() entityType: EntityType;
    @Input() entityTypes: EntityType[];
    @ViewChild('entitySelectionDialog') entitySelectionDialog: TemplateRef<any>;

    pheFields: PheFormData[];
    tableHeader: EntityField[];
    allEntityTypes: EntityType[];
    allEntities: Entity[] = [];
    searchQuery = '';
    filteredEntities: Entity[] = [];
    dialogRef: MatDialogRef<any>;

    constructor(
        private entityTypeService: EntityTypeService,
        private entitiesService: EntitiesService,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit() {
        this.allEntityTypes = this.entityTypes?.length ? this.entityTypes : [this.entityType];

        this.subscriptions.add(
            this.fieldFormData.form.controls.planningHas.valueChanges.subscribe(() => {
                setTimeout(() => {
                    this.filterPhesByEntityType();
                });
            })
        );
        this.loadEntities();
    }

    filterPhesByEntityType() {
        const allEntityTypeKeys = this.allEntityTypes.map(et => et.key);
        this.tableHeader = this.getMergedTableHeaders(this.allEntityTypes);
        this.pheFields = this.fieldFormData.pheFields.filter(pheField =>
            allEntityTypeKeys.includes(pheField.form.controls.phe.get('entitytype_key').value)
        );
    }

    private getMergedTableHeaders(entityTypes: EntityType[]): EntityField[] {
        const fieldMap = new Map<string, EntityField>();

        entityTypes.forEach(entityType => {
            const headers = this.entityTypeService.getTableHeaders(entityType);
            headers.forEach(field => {
                if (!fieldMap.has(field.key)) {
                    fieldMap.set(field.key, field);
                }
            });
        });
        return Array.from(fieldMap.values()).sort((a, b) => a.displayOrder - b.displayOrder);
    }

    private loadEntities() {
        const entityTypeKeys = this.allEntityTypes.map(et => et.key);
        this.entitiesService.getByType(entityTypeKeys).subscribe(entities => {
            this.allEntities = entities;
            this.filteredEntities = entities;
        });
    }

    openAddModal() {
        this.searchQuery = '';
        this.filteredEntities = this.allEntities;
        this.dialogRef = this.dialog.open(this.entitySelectionDialog, {
            width: '600px',
            maxHeight: '80vh',
        });
    }

    filterEntities() {
        if (!this.searchQuery?.length) {
            this.filteredEntities = this.allEntities;
            return;
        }

        const query = this.searchQuery.toLowerCase();
        this.filteredEntities = this.allEntities.filter(entity => entity.name.toLowerCase().includes(query));
    }

    selectEntity(entity: Entity) {
        const entityType = this.allEntityTypes.find(et => entity.entitytypes.some(entType => entType.key === et.key));

        if (entityType) {
            this.addPheWithEntity(entity, entityType);
        }

        this.dialogRef?.close();
    }

    private addPheWithEntity(entity: Entity, entityType: EntityType) {
        const phe = this.entityTypeService.addPheToForm(this.fieldFormData, entityType);
        this.filterPhesByEntityType();
        // timeout so that valueChange on entity Input is triggered
        setTimeout(() => {
            (phe.form.controls.phe.get('entity_id') as any as FormControl).setValue(entity.id);
        });
    }

    addPhe() {
        this.entityTypeService.addPheToForm(this.fieldFormData, this.allEntityTypes[0]);
        this.filterPhesByEntityType();
    }

    removePhe(phe: PheFormData) {
        this.entityTypeService.removePheFromForm(this.fieldFormData, phe);
        this.filterPhesByEntityType();
    }

    getEntityTypeIcon(pheField: PheFormData): string {
        const entityTypeKey = pheField.form.controls.phe.get('entitytype_key').value;
        return this.allEntityTypes.find(et => et.key === entityTypeKey)?.icon;
    }
}
